import { EyeIcon, XIcon } from "@heroicons/react/solid";
import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Pagination from "../../components/pagination";
import PrimaryLoader from "../../components/common/PrimaryLoader";
import toast from "react-hot-toast";
import Modals from "../../components/modal/Modals";
import Moment from "react-moment";
import ZoomImage from "../../components/ZoomImage";
import { InlineLoader } from "../../components/Loader";
import useGet from "../../hooks/read/useGet";
import usePut from "../../hooks/usePut";
import { debounce } from "lodash";
import { Button } from "../../components/ui";
import RoundedContainer from "../../components/containers/RoundedContainer";
import SearchBar from "../../components/inputs/SearchBar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/table/Table";

function LeaveApplied() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [singleLeaveData, setSingleLeaveData] = useState({});
  const [isOpenVerificationModal, setIsOpenVerificationModal] = useState(false);
  const [actionType, setActionType] = useState(null);

  const initialParams = {
    page_no: searchParams.get("page_no") || 0,
    limit: searchParams.get("limit") || 10,
    search_query: searchParams.get("search_query") || "",
  };

  const { list, paramsObject, setGetListParams } = useGet({
    url: "leaveapplicationlist",
    initialData: initialParams,
    onError: (error) => {
      console.error("Error fetching leave applications:", error);
      toast.error("Error fetching leave applications. Please try again later.");
    },
  });

  const { handleAuth, isLoading: isSubmitting } = usePut({
    url: "leaveapprove",
    refreshUrl: "leaveapplicationlist",
    onSuccess: (data) => {
      toast.success(data?.data?.message || "Leave status updated successfully");
      setIsOpenVerificationModal(false);
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || "Error updating leave status"
      );
    },
  });

  const leaves = list.data?.data || [];
  const isLoading = list.isLoading;

  useEffect(() => {
    const searchQuery = searchParams.get("search_query");
    const pageNumber = searchParams.get("page_no");
    const limit = searchParams.get("limit");

    // Only update the URL when search_query or pagination params are meaningful
    if (!searchQuery && (!pageNumber || pageNumber === "0")) {
      setSearchParams({}); // Clear the URL to `/leave-applied`
    } else if (!searchQuery) {
      setSearchParams({ page_no: pageNumber || 0, limit: limit || 10 });
    }
  }, [searchParams]);

  const debouncedOnSearch = useRef(
    debounce((searchQuery) => {
      const updatedParams = {
        ...paramsObject,
        search_query: searchQuery,
        page_no: 0, // Reset to first page on search
      };
      setGetListParams(updatedParams);
      setSearchParams(updatedParams); // Update URL search params
    }, 700)
  ).current;

  const [searchQuery, setSearchQuery] = useState(paramsObject.search_query);

  const handleSearch = (e) => {
    const { value } = e.target;
    setSearchQuery(value); // Update the input field's state immediately
    debouncedOnSearch(value); // Trigger the debounced function
  };

  const handlePageClick = (val) => {
    const updatedParams = {
      ...paramsObject,
      page_no: val.selected,
    };
    setGetListParams(updatedParams);
    setSearchParams(updatedParams);
  };

  const handleGetSingleLeaveData = (id) => {
    setSingleLeaveData(leaves.find((data) => data.id === id));
    setIsOpenVerificationModal(true);
  };

  const handleVerifyLeave = (id, action) => {
    setActionType(action);
    const payload = {
      id,
      action,
    };
    handleAuth.mutate(payload);
  };

  return (
    <>
      <RoundedContainer className="mx-4 mb-4">
        <div className="w-[400px]">
          <SearchBar
            placeholder="Search By User Name..."
            value={searchQuery}
            onChange={handleSearch}
          />
        </div>
      </RoundedContainer>
      {isLoading ? (
        <div className="w-16 h-16 mx-auto my-20">
          <PrimaryLoader />
        </div>
      ) : (
        <>
          <RoundedContainer className="mx-4">
            {leaves.length > 0 ? (
              <div className="max-w-full overflow-x-auto">
                <Table className="table-auto w-full">
                  <TableHead>
                    <TableHeaderCell className="w-1/6">
                      User Name
                    </TableHeaderCell>
                    <TableHeaderCell className="w-1/6">
                      Type of leave
                    </TableHeaderCell>
                    <TableHeaderCell className="w-1/6 text-center">
                      From
                    </TableHeaderCell>
                    <TableHeaderCell className="w-1/6 text-center">
                      To
                    </TableHeaderCell>
                    <TableHeaderCell className="w-1/6 text-center">
                      Status
                    </TableHeaderCell>
                    <TableHeaderCell className="w-1/6 text-center">
                      Action
                    </TableHeaderCell>
                  </TableHead>
                  <TableBody>
                    {leaves.map((data, index) => {
                      const {
                        employee_name,
                        type_of_leave,
                        leave_from,
                        leave_to,
                        id,
                        status,
                      } = data;
                      return (
                        <TableRow key={index}>
                          <TableCell className="text-dark font-medium text-sm">
                            {employee_name}
                          </TableCell>
                          <TableCell className="text-dark font-medium text-sm capitalize">
                            {type_of_leave}
                          </TableCell>
                          <TableCell className="text-center text-dark font-medium text-sm capitalize">
                            <Moment unix format="DD/MM/yyyy">
                              {leave_from}
                            </Moment>
                          </TableCell>
                          <TableCell className="text-center text-dark font-medium text-sm capitalize">
                            {leave_to !== "0" ? (
                              <Moment unix format="DD/MM/yyyy">
                                {leave_to}
                              </Moment>
                            ) : (
                              "---"
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {status === "1" ? (
                              <span className="bg-green-100 text-green-700 font-medium px-2.5 py-1 rounded-full text-xs">
                                Approved
                              </span>
                            ) : status === "2" ? (
                              <span className="bg-yellow-100 text-yellow-700 font-medium px-2.5 py-1 rounded-full text-xs">
                                Pending
                              </span>
                            ) : (
                              <span className="bg-red-100 text-red-700 font-medium px-2.5 py-1 rounded-full text-xs">
                                Rejected
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center items-center">
                              <button
                                type="button"
                                className="cursor-pointer text-primary hover:text-blue-700 p-1 rounded hover:bg-gray-100 transition-colors"
                                onClick={() => handleGetSingleLeaveData(id)}
                              >
                                <EyeIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-16 text-lg">
                No leave applications found.
              </div>
            )}
          </RoundedContainer>
          {list.data?.total_count > paramsObject.limit && (
            <RoundedContainer className="mx-4 mt-4">
              <Pagination
                lengthofItems={list.data?.total_count}
                limit={paramsObject.limit}
                currentPage={paramsObject.page_no}
                onPageChange={handlePageClick}
              />
            </RoundedContainer>
          )}
        </>
      )}
      <Modals show={isOpenVerificationModal}>
        <div className=" divide-y">
          <div className="modalHead pb-3">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Leave Verification</h2>
              <div
                className="w-6 h-6 cursor-pointer"
                onClick={() => setIsOpenVerificationModal(false)}
              >
                <XIcon className="w-full h-full" />
              </div>
            </div>
          </div>
          <div className="modalBody py-3 space-y-4">
            <div>
              <label htmlFor="employeeName" className="label">
                Employee Name :{" "}
              </label>
              <span className="text-lg font-semibold">
                {" "}
                {singleLeaveData?.employee_name}
              </span>
            </div>
            <div>
              <label htmlFor="leave_dates" className="label">
                Leave Dates :{" "}
              </label>
              <span className="text-lg font-semibold inline-block mx-2 bg-gray-300 px-4 py-2 rounded">
                <span>
                  {" "}
                  {singleLeaveData?.type_of_leave === "CL"
                    ? "On : "
                    : "From : "}{" "}
                </span>
                <small>
                  <Moment unix format="DD/MM/yyyy">
                    {singleLeaveData?.leave_from}
                  </Moment>
                </small>
              </span>
              {singleLeaveData?.type_of_leave === "CL" ? null : (
                <span className="text-lg font-semibold inline-block ml-2 bg-gray-300 px-4 py-2 rounded">
                  <span> To : </span>
                  <small>
                    {singleLeaveData?.leave_to !== "0" ? (
                      <Moment unix format="DD/MM/yyyy">
                        {singleLeaveData?.leave_to}
                      </Moment>
                    ) : (
                      "---"
                    )}
                  </small>
                </span>
              )}
            </div>
            <div>
              <label className="label">Leave Duration :&nbsp;</label>
              <span className="text-lg font-semibold">
                {singleLeaveData?.total_leave} {Number(singleLeaveData?.total_leave) > 1 ? "Days" : "Day"}
                {singleLeaveData?.is_half_day === "1" &&
                singleLeaveData?.half_day_type &&
                singleLeaveData?.half_day_type !== "null" ? (
                  ` (Half Day - ${
                    singleLeaveData?.half_day_type === "FIRST_HALF"
                      ? "First Half"
                      : singleLeaveData?.half_day_type === "SECOND_HALF"
                      ? "Second Half"
                      : singleLeaveData?.half_day_type
                  })`
                ) : (
                  ""
                )}
              </span>
            </div>
            <div>
              <label htmlFor="employeeName" className="label">
                Reason for leave :&nbsp;
              </label>
              <span className="text-lg font-semibold">
                {singleLeaveData?.reason}
              </span>
            </div>
            {(singleLeaveData?.type_of_leave === "SL" ||
              singleLeaveData?.type_of_leave === "OL" ||
              singleLeaveData?.type_of_leave === "other") &&
              singleLeaveData?.file_list &&
              singleLeaveData?.file_list.length > 0 && (
                <div className="Medical Certificate">
                  <label htmlFor="employeeName" className="label">
                    Medical Certificate :{" "}
                  </label>
                  <div className="grid grid-cols-4 gap-4">
                    {singleLeaveData?.file_list.map((src, idx) => {
                      const { file_name, document_media_type } = src;
                      return (
                        <div key={idx}>
                          {document_media_type === "pdf" ? (
                            <a
                              href={file_name}
                              target="_blank"
                              rel="noopener noreferrer"
                              className=" text-center py-2 px-2 rounded bg-gray-400 text-gray-700"
                            >
                              View PDF
                            </a>
                          ) : (
                            <ZoomImage className="h-full">
                              <img
                                src={file_name}
                                alt="medical certificate"
                                className="border-2 border-black w-full h-full"
                              />
                            </ZoomImage>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
          </div>
          <div className="modalFoot pt-3">
            <div className=" space-x-4">
              <Button
                variant="default"
                disabled={isSubmitting}
                onClick={() =>
                  handleVerifyLeave(singleLeaveData?.id, "APPROVE")
                }
              >
                {isSubmitting && actionType === "APPROVE" ? (
                  <>
                    <InlineLoader className="text-white mr-2" />
                    Verifying...
                  </>
                ) : (
                  "Verify"
                )}
              </Button>
              <Button
                variant="outline"
                disabled={isSubmitting}
                onClick={() =>
                  handleVerifyLeave(singleLeaveData?.id, "REJECT")
                }
              >
                {isSubmitting && actionType === "REJECT" ? (
                  <>
                    <InlineLoader className="text-primary mr-2" />
                    Rejecting...
                  </>
                ) : (
                  "Reject"
                )}
              </Button>
            </div>
          </div>
        </div>
      </Modals>
    </>
  );
}

export default LeaveApplied;
